import { ValidSavedView } from "@/features/saved-views/apply-saved-view";
import { ModeNames } from "@/modes/mode";
import { clearStore } from "@faro-lotv/app-component-toolbox";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";

/**
 * A set of parameters that can be passed to a mode during initialization to customize its behavior
 *
 * For example the initial camera parameters from a deep link
 */
export type ModeInitialState = Record<string, string | undefined>;

/**
 * Shared state of the main 3d view
 */
export type ModeState = {
  /** Name of the current active mode */
  name: ModeNames;

  /** The initial state to use to initialize the mode */
  initialState: ModeInitialState | undefined;

  /** True if the mode is suspending to load new data */
  isLoadingData: boolean;

  /** True if the mode is transitioning to another mode */
  isTransitioning: boolean;

  /** The saved view the app was most recently opened with, resolved against the project */
  savedView: ValidSavedView | undefined;

  /**
   * The saved view that has already been applied.
   *
   * The start mode is not only the entry point: several workflows return to it
   * mid-session. A saved view is the whole truth about the view it describes, so
   * applying it clears the filters, clipping box and tool the user set in the
   * meantime, which must not happen on such a return.
   *
   * Recording *which* view was applied rather than merely that one was is what
   * keeps that guard from blocking the next view: opening a second shared view in
   * the same session has to apply it, even though another one was applied before.
   */
  appliedSavedView: ValidSavedView | undefined;
};

export const MODE_SLICE_INITIAL_STATE: ModeState = {
  name: "start",
  initialState: undefined,
  isLoadingData: false,
  isTransitioning: false,
  savedView: undefined,
  appliedSavedView: undefined,
};

/**
 * Slice to access global information about the current state of the main 3d view
 */
const modeSlice = createSlice({
  name: "mode",
  initialState: MODE_SLICE_INITIAL_STATE,
  reducers: {
    /**
     * Change the current active camera the user is interacting with
     *
     * @param state Current state
     * @param action The new mode to transition to, with an optional initial state
     *
     * NOTE: The payload can be a simple ModeName instead of an object to allow the action to remain
     * compatible with the code used before the introduction of the initialState
     */
    changeMode(
      state,
      action: PayloadAction<
        ModeNames | { mode: ModeNames; initialState?: ModeInitialState }
      >,
    ) {
      if (typeof action.payload === "string") {
        state.name = action.payload;
        state.initialState = undefined;
      } else {
        state.name = action.payload.mode;
        state.initialState = action.payload.initialState;
      }
    },
    /**
     * Change the current state of the flag isLoadingData
     *
     * @param state Current state
     * @param action True if the mode is loading data
     */
    setModeIsLoadingData(state, action: PayloadAction<boolean>) {
      state.isLoadingData = action.payload;
    },
    /**
     * Change the current state of the flag isTransitioning
     *
     * @param state Current state
     * @param action True if the mode is loading data
     */
    setModeIsTransitioning(state, action: PayloadAction<boolean>) {
      state.isTransitioning = action.payload;
    },
    /**
     * Store the saved view the app was opened with, resolved against the project
     *
     * @param state Current state
     * @param action The resolved saved view
     */
    setSavedView(state, action: PayloadAction<ValidSavedView>) {
      state.savedView = action.payload;
    },
    /**
     * Record which saved view has been applied, so the same one is not applied
     * again when the app returns to the start mode
     *
     * @param state Current state
     * @param action The view that was just applied
     */
    savedViewApplied(state, action: PayloadAction<ValidSavedView>) {
      state.appliedSavedView = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearStore, () => MODE_SLICE_INITIAL_STATE);
  },
});

export const modeReducer = modeSlice.reducer;

export const {
  changeMode,
  setModeIsLoadingData,
  setModeIsTransitioning,
  setSavedView,
  savedViewApplied,
} = modeSlice.actions;

/**
 * Kept next to the slice rather than in `mode-selectors.ts`, which pulls in the
 * mode registry and so cannot be imported from a mode.
 *
 * Compared by value: two share links can name structurally identical views, and
 * applying the one when the other is already in place would be a no-op anyway.
 *
 * @param view The view about to be applied.
 * @returns whether that view is the one already applied.
 */
export function selectIsSavedViewApplied(view: ValidSavedView) {
  return (state: {
    /** The mode slice */
    mode: ModeState;
  }): boolean => isEqual(state.mode.appliedSavedView, view);
}
